import { ChangeEvent, FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useLocalStorageState from "use-local-storage-state";

import { faAdd, faColumns3, faSearch, faSliders } from "@fortawesome/pro-regular-svg-icons";

import { TextField } from "@mui/material";

import { format } from "date-fns";

import { Icon, LoadingOverlay, Modal } from "@/components";

import { Table, TableColumn } from "@/components/Table/Table";
import { useBulkFunctions } from "@/utils/userBulkFunctions";
import { SortParams, Order } from "@/types/types";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
	clearRecords,
	profileTrackerSelector,
} from "@/pages/Private/redux/profileTracker/profileTracker.slice";

import { Button } from "@/components/Button/Button";

import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { TrackerSourceType } from "@/enum/tracker.enum";

import { useCopyToClipboard } from "@/utils/useCopyToClipboard";

import { InputField } from "@/components/InputField/InputField";

import { TItem } from "@/components/SortableList/SortableList";

import { useSnackBar } from "@/utils/useSnackbar";

import {
	useDeleteColumnMutation,
	useLazyGetTrackerOrganizationsQuery,
	useRemoveHoldingToOrganizationMutation,
	useGetTrackerQuery,
} from "@/pages/Private/redux/profileTracker/profileTracker.api";

import { useGetSignalConfigQuery } from "@/pages/Private/redux/signal/signal.api";

import {
	useCreateTableConfigMutation,
	useLazyGetTableConfigQuery,
	useUpdateTableConfigMutation,
} from "@/pages/Private/redux/tableConfig/tableConfig.api";

import { CustomColumnTypes, SignalFilterStatus } from "@/enum/list.enum";

import {
	DropdownWithCheckboxes,
	DropdownWithCheckboxesItem,
} from "@/components/DropdownWithCheckboxes/DropdownWithCheckboxes";

import { tableConfigSelector } from "@/pages/Private/redux/tableConfig/tableConfig.slice";

import { FilterModal } from "./FilterModal";
import { buildTrackerColumnConfig } from "../utils/buildTrackerColumnConfig";
import { TrackerOrganization } from "../schema/trackerOrganizations";
import { GeneralTableColumns } from "./GeneralTableColumns";
import { CreateCustomColumnModal } from "./CustomColumns/Modals/CreateCustomColumnModal";
import { CustomBooleanColumn } from "./CustomColumns/CustomBooleanColumn";
import { CustomNumberColumn } from "./CustomColumns/CustomNumberColumn";
import { CustomLinkColumn } from "./CustomColumns/CustomLinkColumn";
import { CustomTextColumn } from "./CustomColumns/CustomTextColumn";
import { getOrderStatus } from "../../List/utils/column-utils";
import { areTableConfigEntriesEqual, buildTableConfigEntries } from "../utils/trackerTableConfig";
import { ColumnModal } from "./CustomColumns/ColumnModal";
import { EditCustomColumnModalWrapper } from "./CustomColumns/Modals/EditCustomColumnModalWrapper";
import { DetailView } from "./CustomColumns/DetailView";
import { getSvgIcons } from "../utils/getIconsByType";
import { CustomTextColumnReadOnly } from "./CustomColumns/CustomTextColumnReadOnly";
import { SearchForCompanyModal } from "./CustomColumns/Modals/SearchForCompanyModal";
import { useTrackerBulkActions } from "../hooks/useTrackerBulkActions";
import { SearchForCompanyHoldingModal } from "./CustomColumns/Modals/SearchForCompanyHoldingModal";
import { CustomDepartmentSizeColumn } from "./CustomColumns/CustomDepartmentSizeColumn";
import { useTrackerUrlState } from "../hooks/useTrackerUrlState";
import { useTrackerTableIds } from "../hooks/useTrackerTableIds";

interface TrackerDetailsTableProps {
	trackerType?: TrackerSourceType;
	/** Optional tracker ID. If provided, overrides the URL param id */
	trackerId?: number;
	/**
	 * Optional list ID for the organizations query (its `/tracker-organization/:id`
	 * path param IS a list id). On the tracker page it comes from the URL; hosts
	 * embedding the table elsewhere (e.g. the Feed's Tracked Profiles tab, where
	 * `useParams` has no id) must pass it explicitly or the query hits `/undefined`.
	 */
	listId?: number;
}

export interface FilterTrackerDetailsTable {
	name?: string;
	email?: string;
	linkedinUrl?: string;
	country?: string;
	city?: string;
	state?: string;
	company?: string;
	companyCountry?: string;
	companyState?: string;
	companyCity?: string;
	companyLinkedinUrl?: string;
	title?: string;
	seniority?: string;
	website?: string;
	event?: {
		name: string;
		id: number;
	}[];
}

export const TrackerDetailsTable: FunctionComponent<TrackerDetailsTableProps> = ({
	trackerType,
	trackerId: trackerIdProp,
	listId: listIdProp,
}) => {
	const { t } = useTranslation();
	const { trackerId, listId } = useTrackerTableIds(trackerIdProp, listIdProp);
	const ts = useCallback((key: string) => t(`inbox.${key}`), [t]);
	const { records: data, recordsMeta: meta } = useAppSelector(profileTrackerSelector);

	const { data: tableConfiguration } = useAppSelector(tableConfigSelector);

	const { renderSnackbar, handleCopyClick } = useCopyToClipboard();
	const { renderMessageSnackbar, handleOpenSnackbar } = useSnackBar();
	const {
		page,
		setPage,
		limit,
		setLimit,
		sorting: sortingRecords,
		setSorting,
		filter,
		setFilter,
		signalFilterStatus,
		setSignalFilterStatus,
		searchValue,
		setSearchValue,
		query,
		searchParams,
		setSearchParams,
	} = useTrackerUrlState({
		prefix: "tracker",
		defaultSortBy: "updatedAt",
		defaultSortOrder: Order.DESC,
	});

	const [customColumnTextValue, setCustomColumnTextValue] = useState<string>();
	const [customColumnNumberValue, setCustomColumnNumberValue] = useState<number | null>();
	const [showSelectColumns, setShowSelectColumns] = useState(false);

	const dispatch = useAppDispatch();
	const {
		selectedItems,
		selectAll,
		handleSelectAll,
		handleSelectItem,
		toggleSelectAll,
		setSelectedItems,
	} = useBulkFunctions();

	const [selectedTrackerOrganization, setSelectedTrackerOrganization] = useState<
		TrackerOrganization | undefined
	>();
	const [showNewColumn, setShowNewColumn] = useState<boolean>(false);
	const [showNewCompany, setShowNewCompany] = useState<boolean>(false);
	const [editableColumn, setEditableColumn] = useState<TItem>();
	const [showEditColumn, setShowEditColumn] = useState<boolean>(false);
	const [showDetailView, setShowDetailView] = useState<boolean>(false);
	const [showDeleteColumn, setShowDeleteColumn] = useState<boolean>(false);

	const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
	const [showEdit, setShowEdit] = useState<{ id: number; name: string }>();
	const [showEditHolding, setShowEditHolding] = useState<{ id: number; name: string }>();
	const [showDeleteHolding, setShowDeleteHolding] = useState<{ id: number; name: string }>();
	const [tableConfig, setTableConfig] = useLocalStorageState<TItem[]>(
		`tracker-column-settings-${trackerId}`,
		{
			defaultValue: [],
		}
	);

	const [removeHolding, { isLoading: removeHoldingLoading }] =
		useRemoveHoldingToOrganizationMutation();
	const [getTrackerOrganization, { isLoading, isFetching }] = useLazyGetTrackerOrganizationsQuery();

	const [createConfig, { isLoading: isCreatingLoading }] = useCreateTableConfigMutation();
	const [updateConfig, { isLoading: isUpdatingLoading }] = useUpdateTableConfigMutation();
	const [deleteColumn, { isLoading: deleteLoading }] = useDeleteColumnMutation();

	const handleSorting = (sortParams: SortParams) => {
		setSorting(sortParams);
	};

	const { data: tracker } = useGetTrackerQuery(trackerId ?? 0);

	// PREC-3616 — one request per table (RTK-cached), never per row: the endpoint answers with an
	// empty config (`id: null`) for a list that has none, and without a config no scan ever runs,
	// so the Signal column must not promise one.
	const { data: signalConfig } = useGetSignalConfigQuery(listId ?? 0, { skip: !listId });
	const signalsConfigured = signalConfig?.id != null;

	const [getTableConfig, { isLoading: isLoadingConfig }] = useLazyGetTableConfigQuery();

	useEffect(() => {
		if (trackerId) {
			getTableConfig({ id: trackerId, source: "tracker" });
		}
	}, [trackerId, getTableConfig]);

	useEffect(() => {
		if (trackerId && !isLoadingConfig && !isCreatingLoading) {
			if (data && data.length > 0) {
				let newConfig =
					tableConfig.length > 0 && tableColumns.length === tableConfig.length
						? tableConfig
						: buildTrackerColumnConfig(tableColumns ?? []);

				const foundColumn =
					(newConfig as TItem[]).every((c: { content: string }) =>
						tableColumns?.find((u: TableColumn) => u.title === c?.content)
					) &&
					tableColumns.every((c) => (newConfig as TItem[]).find((u) => u.content === c.title)) &&
					tableConfig.every((c) => (newConfig as TItem[]).find((u) => u.content === c.content)) &&
					(newConfig as TItem[]).every((c) => tableConfig.find((u) => u.content === c.content));

				if (!foundColumn || tableConfig.length === 0) {
					newConfig = buildTrackerColumnConfig(tableColumns ?? []);

					if (newConfig.length > 0) {
						setTableConfig(newConfig);

						createConfig({
							trackerId: trackerId,
							config: newConfig,
						});
					}
				}
			}
		}
	}, [tableConfiguration, isLoadingConfig, isCreatingLoading, trackerId, data]);

	useEffect(() => {
		dispatch(clearRecords());

		getTrackerOrganization({
			page,
			limit,
			searchValue: query,
			sorting: sortingRecords,
			trackerId: trackerId,
			listId,
			filter,
			signalFilterStatus: signalFilterStatus ?? undefined,
		});
	}, [
		dispatch,
		filter,
		getTrackerOrganization,
		trackerId,
		limit,
		page,
		query,
		sortingRecords,
		listId,
		signalFilterStatus,
	]);

	useEffect(() => {
		if (selectAll) {
			handleSelectAll(data?.map((item) => item.id) || []);
		} else {
			handleSelectAll([]);
		}
	}, [selectAll, data]);

	const [previousColumnCount, setPreviousColumnCount] = useState(0);

	useEffect(() => {
		if (
			(tableConfiguration?.config && !isUpdatingLoading) ||
			tableConfiguration?.config?.length === 0
		) {
			updateTableConfig(tableColumns);
		}
	}, [tableConfig]);

	const updateTableConfig = useCallback(
		(columns: TableColumn[]) => {
			const newConfig = buildTableConfigEntries(columns);

			if (
				(tableConfiguration?.config &&
					!areTableConfigEntriesEqual(newConfig, tableConfiguration?.config)) ||
				tableConfiguration?.config?.length === 0
			) {
				updateConfig({
					id: tableConfiguration.id,
					trackerId: trackerId ?? 0,
					config: newConfig,
				});
			}
		},

		[setTableConfig, tableConfig]
	);

	const handlePersonClick = useCallback(
		(trackerOrganizationId: number, personId: number) => {
			const selectedRow = data.find((row) => row.id === trackerOrganizationId);

			// Set selectedPersonId in URL
			const newParams = new URLSearchParams(searchParams);

			newParams.set("selectedPersonId", personId.toString());
			setSearchParams(newParams, { replace: true });

			setSelectedTrackerOrganization(selectedRow);
			setShowDetailView(true);
		},
		[data, searchParams, setSearchParams]
	);

	const handleOpenResearchTab = useCallback(
		(row: TrackerOrganization) => {
			const newParams = new URLSearchParams(searchParams);

			newParams.set("detailTab", "RESEARCH");

			// Pre-select the person that has a deep research response
			const personWithResearch = row.trackerPersons?.find(
				(tp) =>
					tp.prospect?.deepResearchResponse?.response &&
					Object.keys(tp.prospect.deepResearchResponse.response).length > 0
			);

			if (personWithResearch?.prospect?.id) {
				newParams.set("selectedPersonId", personWithResearch.prospect.id.toString());
			}

			setSearchParams(newParams, { replace: true });
			setSelectedTrackerOrganization(row);
			setShowDetailView(true);
		},
		[searchParams, setSearchParams]
	);

	const tableColumns: TableColumn[] = useMemo(() => {
		const getColumnsBasedOnTrackerType = () => {
			const columns: TableColumn[] = GeneralTableColumns({
				selectAll,
				toggleSelectAll,
				selectedItems,
				handleSelectItem,
				handleCopyClick,
				// handleContextMenu,
				ts,
				sortingInbox: sortingRecords,
				setShowEdit: setShowEditHolding,
				showEdit: showEditHolding,
				onPersonClick: handlePersonClick,
				onResearchClick: handleOpenResearchTab,
				signalsConfigured,
			});

			return columns;
		};

		let columns: TableColumn[] = getColumnsBasedOnTrackerType();

		if (data && data.length > 0) {
			for (const rec of data) {
				if (rec.tracker.columns) {
					for (const customColumn of rec.tracker.columns) {
						const hasColumn = columns.find((c) => c.uid === `custom-${customColumn.id}`);

						if (!hasColumn) {
							const type = customColumn.type as CustomColumnTypes;
							const title = customColumn.name;
							const width = "fit-content";
							const minWidth = "100px";
							const uid = `custom-${customColumn.id}`;
							const titleUpdated = `${customColumn.name} last update`;
							const uidUpdated = `updated-${customColumn.id}`;
							const show = true;
							const showLastUpdate = false;
							const moveable = true;
							const hideable = true;
							const orderTitle = `custom-${customColumn.id}`;

							const orderStatus = getOrderStatus(sortingRecords, orderTitle);

							const currentSvgIcon = getSvgIcons(type);

							const render = (row: TrackerOrganization) => {
								const currentCustomColumn = row.tracker.columns?.find(
									(c) => c.id === customColumn.id
								);

								const columnValue = row.columns?.find(
									(c) => c.columnConfigId === currentCustomColumn?.id
								);

								return (
									<div className="flex flex-row items-start justify-start max-w-[300px]">
										<div className="flex-col items-start justify-start w-full">
											{(type === CustomColumnTypes.TEXT ||
												type === CustomColumnTypes.JOB_OFFER ||
												type === CustomColumnTypes.TECHNOLOGY ||
												type === CustomColumnTypes.RANDOM_VALUE) &&
												currentCustomColumn && (
													<CustomTextColumn
														columnConfig={currentCustomColumn}
														columnValue={columnValue}
														customColumnTextValue={customColumnTextValue}
														row={row}
														setCustomColumnTextValue={setCustomColumnTextValue}
														setShowEdit={setShowEdit}
														showEdit={showEdit}
														type={type}
													/>
												)}
											{(type === CustomColumnTypes.GOOGLE_NEWS ||
												type === CustomColumnTypes.AI_PROMPT ||
												type === CustomColumnTypes.JOB_CHANGE ||
												type === CustomColumnTypes.LINKEDIN_POST) &&
												currentCustomColumn && (
													<CustomTextColumnReadOnly columnValue={columnValue} />
												)}
											{type === CustomColumnTypes.LINK && currentCustomColumn && (
												<CustomLinkColumn
													columnConfig={currentCustomColumn}
													columnValue={columnValue}
													customColumnTextValue={customColumnTextValue}
													row={row}
													setCustomColumnTextValue={setCustomColumnTextValue}
													setShowEdit={setShowEdit}
													showEdit={showEdit}
													type={type}
												/>
											)}
											{type === CustomColumnTypes.NUMBER && currentCustomColumn && (
												<CustomNumberColumn
													columnConfig={currentCustomColumn}
													columnValue={columnValue}
													customColumnNumberValue={customColumnNumberValue}
													row={row}
													setCustomColumnNumberValue={setCustomColumnNumberValue}
													setShowEdit={setShowEdit}
													showEdit={showEdit}
													type={type}
												/>
											)}
											{type === CustomColumnTypes.DEPARTMENT_SIZE && currentCustomColumn && (
												<CustomDepartmentSizeColumn
													columnConfig={currentCustomColumn}
													columnValue={columnValue}
													customColumnNumberValue={customColumnNumberValue}
													row={row}
													setCustomColumnNumberValue={setCustomColumnNumberValue}
													setShowEdit={setShowEdit}
													showEdit={showEdit}
													type={type}
												/>
											)}

											{type === CustomColumnTypes.BOOLEAN && currentCustomColumn && (
												<CustomBooleanColumn
													columnConfig={currentCustomColumn}
													columnValue={columnValue}
													row={row}
													type={type}
												/>
											)}
										</div>
									</div>
								);
							};

							columns = [
								...columns,
								{
									title,
									uid,
									minWidth,
									width,
									render,
									orderTitle,
									show,
									moveable,
									hideable,
									orderStatus,
									svgIcon: currentSvgIcon,
									setEditableColumn:
										type !== CustomColumnTypes.JOB_CHANGE ? setEditableColumn : undefined,
									setShowEditColumn:
										type !== CustomColumnTypes.JOB_CHANGE ? setShowEditColumn : undefined,
								},
							];

							const renderUpdatedAt = (row: TrackerOrganization) => {
								const currentCustomColumn = row.tracker.columns?.find(
									(c) => c.id === customColumn.id
								);

								const columnValue = row.columns?.find(
									(c) => c.columnConfigId === currentCustomColumn?.id
								);

								return (
									<div className="flex flex-row items-start justify-start">
										<div className="flex-col items-start justify-start w-full">
											{columnValue?.createdAt
												? format(new Date(columnValue.createdAt), "HH:mm dd.MM.yyyy")
												: ""}
										</div>
									</div>
								);
							};

							columns = [
								...columns,
								{
									title: titleUpdated,
									uid: uidUpdated,
									minWidth,
									width,
									render: renderUpdatedAt,
									orderTitle,
									show: showLastUpdate,
									moveable,
									hideable,
									orderStatus,
									svgIcon: currentSvgIcon,
								},
							];
						}
					}
				}
			}
		}

		if (tableConfig?.length) {
			for (const c of columns) {
				const configIndex = tableConfig.findIndex((tc) => tc?.uid === c.uid);
				const currentIndex = columns.findIndex((tc) => tc?.uid === c.uid);

				if (configIndex > -1 && currentIndex > -1 && configIndex !== currentIndex) {
					const newConfigColumn = columns[currentIndex];

					columns.splice(currentIndex, 1);

					columns = [
						...columns.slice(0, configIndex),
						newConfigColumn,
						...columns.slice(configIndex),
					];
				}
			}

			columns = columns.map((c) => {
				const config = tableConfig.find((tc) => {
					return tc?.uid === c?.uid;
				});

				if (config) {
					c.show = config.show;
					c.locked = config.locked;
				}

				return c;
			});
		}

		return columns;
	}, [
		trackerType,
		data,
		tableConfig,
		selectAll,
		toggleSelectAll,
		selectedItems,
		handleSelectItem,
		handleCopyClick,
		ts,
		sortingRecords,
		showEdit,
		customColumnTextValue,
		customColumnNumberValue,
		handlePersonClick,
		signalsConfigured,
	]);

	useEffect(() => {
		if (data.length !== 0) {
			const currentColumnCount = data[0]?.tracker?.columns?.length || 0;

			if (currentColumnCount !== previousColumnCount) {
				updateTableConfig(tableColumns);
				setPreviousColumnCount(currentColumnCount);
			}
		}
	}, [data, previousColumnCount, updateTableConfig, tableColumns]);

	const handleDeleteColumn = async () => {
		await deleteColumn({ id: editableColumn?.columnId }).unwrap();
		setEditableColumn(undefined);
	};

	const handleRowClick = (id: number) => {
		const selectedRow = data.find((row) => row.id === id);

		// Clear selectedPersonId when switching rows - will default to first person
		const newParams = new URLSearchParams(searchParams);

		newParams.delete("selectedPersonId");
		setSearchParams(newParams, { replace: true });

		setSelectedTrackerOrganization(selectedRow);
		setShowDetailView(true);
	};

	const handleRemoveHoldingFromOrganization = async (id: number) => {
		await removeHolding({
			id: id,
		}).unwrap();

		setShowDeleteHolding(undefined);
	};

	const bulk = useTrackerBulkActions({
		selectedItems,
		selectAll,
		totalCount: meta?.itemCount,
		signalsConfigured,
		clearSelection: () => handleSelectAll([]),
		notify: handleOpenSnackbar,
	});

	return isLoading ? (
		<LoadingOverlay />
	) : (
		<div className="w-full">
			<div className="flex items-center gap-2 h-11 px-2 bg-white border-b border-border-light">
				<div className="w-[280px] flex-shrink-0">
					<InputField
						className="!rounded-[10px]"
						classNameWrapper="!mb-0"
						handleChange={(e: ChangeEvent<HTMLInputElement>) => {
							setSearchValue(e.target.value);
						}}
						icon={faSearch}
						name={"search"}
						placeholder="Search..."
						size={ButtonSize.XS}
						value={searchValue}
					/>
				</div>
				<div className="w-fit min-w-[120px] max-w-[220px] flex-shrink-0">
					<DropdownWithCheckboxes
						size={ButtonSize.XS}
						data={[
							{
								title: ts(`signalFilterOptions.${SignalFilterStatus.ALL}`),
								id: SignalFilterStatus.ALL,
							},
							{
								title: ts(`signalFilterOptions.${SignalFilterStatus.ALL_WITH_SIGNAL}`),
								id: SignalFilterStatus.ALL_WITH_SIGNAL,
							},
							{
								title: ts(`signalFilterOptions.${SignalFilterStatus.S1}`),
								id: SignalFilterStatus.S1,
							},
							{
								title: ts(`signalFilterOptions.${SignalFilterStatus.S2}`),
								id: SignalFilterStatus.S2,
							},
							{
								title: ts(`signalFilterOptions.${SignalFilterStatus.S3}`),
								id: SignalFilterStatus.S3,
							},
							{
								title: ts(`signalFilterOptions.${SignalFilterStatus.DQ}`),
								id: SignalFilterStatus.DQ,
							},
						]}
						defaultValue={[
							{
								title: ts(`signalFilterOptions.${SignalFilterStatus.ALL}`),
								id: SignalFilterStatus.ALL,
							},
						]}
						floating={true}
						handleSelect={(values: DropdownWithCheckboxesItem[]) => {
							const addedValue = values.find(
								(val) => !signalFilterStatus.includes(val.id as SignalFilterStatus)
							);

							const newValues = values.filter(
								(val) =>
									val.id !== SignalFilterStatus.ALL && val.id !== SignalFilterStatus.ALL_WITH_SIGNAL
							);

							if (addedValue && addedValue.id === SignalFilterStatus.ALL) {
								setSignalFilterStatus([SignalFilterStatus.ALL]);
							} else if (addedValue && addedValue.id === SignalFilterStatus.ALL_WITH_SIGNAL) {
								setSignalFilterStatus([SignalFilterStatus.ALL_WITH_SIGNAL]);
							} else {
								setSignalFilterStatus(newValues.map((v) => v.id as SignalFilterStatus));
							}
						}}
						value={
							signalFilterStatus && signalFilterStatus.length > 0
								? signalFilterStatus.map((i) => {
										return {
											title: ts(`signalFilterOptions.${i}`),
											id: i,
										};
									})
								: [
										{
											title: ts(`signalFilterOptions.${SignalFilterStatus.ALL}`),
											id: SignalFilterStatus.ALL,
										},
									]
						}
					/>
				</div>
				<div className="ml-auto flex items-center gap-2">
					<div className="flex-shrink-0">
						<Button
							color={ButtonColor.BRAND}
							image={<Icon className="mr-2" icon={faAdd} />}
							size={ButtonSize.S}
							title={ts("addCompany")}
							onClick={() => setShowNewCompany(true)}
						/>
					</div>
					<div className="flex-shrink-0">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="mr-2" icon={faColumns3} />}
							size={ButtonSize.S}
							title={ts("columns")}
							onClick={() => setShowSelectColumns(true)}
						/>
					</div>
					<div className="flex-shrink-0">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="mr-2" icon={faSliders} />}
							size={ButtonSize.S}
							title={`${ts("filter")}${
								filter && Object.keys(filter).length > 0 ? ` (${Object.keys(filter).length})` : ""
							}`}
							onClick={() => setShowFilterModal(true)}
						/>
					</div>

					<div className="flex-shrink-0">
						<Button
							color={ButtonColor.BRAND}
							image={<Icon className="mr-2" icon={faAdd} />}
							size={ButtonSize.S}
							title={ts("addColumn")}
							onClick={() => setShowNewColumn(true)}
						/>
					</div>
				</div>
			</div>

			{showFilterModal && trackerId && (
				<FilterModal
					filter={filter}
					inboxData={data}
					setFilter={setFilter}
					setShowFilterModal={setShowFilterModal}
					showFilterModal={showFilterModal}
					trackerId={trackerId}
				/>
			)}

			{showDetailView && selectedTrackerOrganization && (
				<DetailView
					selectedTrackerOrganization={selectedTrackerOrganization}
					setShowDetailView={setShowDetailView}
					signalFilterStatus={signalFilterStatus}
				/>
			)}

			{showEditColumn && trackerId && editableColumn?.columnId && (
				<EditCustomColumnModalWrapper
					columnId={editableColumn?.columnId}
					open={showEditColumn}
					selectedTrackerOrganizationIds={selectedItems}
					setOpen={setShowEditColumn}
					tracker={tracker}
					trackerId={trackerId}
				/>
			)}

			{showSelectColumns && tableConfig && setShowSelectColumns && (
				<ColumnModal
					setEditableColumn={setEditableColumn}
					setShowDeleteColumn={setShowDeleteColumn}
					setShowEditColumn={setShowEditColumn}
					setShowSelectColumns={setShowSelectColumns}
					setTableConfig={setTableConfig}
					showSelectColumns={showSelectColumns}
					tableConfig={tableConfig}
				/>
			)}

			{showNewColumn && trackerId && (
				<CreateCustomColumnModal
					filter={filter}
					listId={listId}
					open={showNewColumn}
					selectedTrackerOrganizationIds={selectedItems}
					setOpen={setShowNewColumn}
					tracker={tracker}
					trackerId={trackerId}
				/>
			)}

			{showNewCompany && trackerId && (
				<SearchForCompanyModal
					open={showNewCompany}
					setOpen={setShowNewCompany}
					trackerId={trackerId}
				/>
			)}

			{showEditHolding && trackerId && (
				<SearchForCompanyHoldingModal
					open={!!showEditHolding}
					setOpen={() => setShowEditHolding(undefined)}
					trackerOrganizationId={showEditHolding.id}
				/>
			)}

			{showDeleteColumn && trackerId && editableColumn?.columnId && (
				<Modal
					handleClose={() => setShowDeleteColumn(false)}
					handleSave={handleDeleteColumn}
					isLoading={deleteLoading}
					isOpened={showDeleteColumn}
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText={t("basics.delete")}
					title={ts("delete.title")}
				>
					<div className="text-sm text-gray-700 mb-4">{ts("delete.confirmation")}</div>
					<TextField
						fullWidth
						defaultValue={editableColumn.content}
						inputProps={{ readOnly: true }}
						label={ts("name")}
					/>
				</Modal>
			)}

			{showDeleteHolding && trackerId && showDeleteHolding?.id && (
				<Modal
					handleClose={() => setShowDeleteHolding(undefined)}
					handleSave={() => handleRemoveHoldingFromOrganization(showDeleteHolding.id)}
					isLoading={removeHoldingLoading}
					isOpened={!!showDeleteHolding}
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText={"Remove"}
					title={"Remove holding company"}
				>
					<div className="text-sm text-gray-700 mb-4">
						Are you sure that you want to remove this holding?
					</div>
					<TextField
						fullWidth
						defaultValue={showDeleteHolding.name}
						inputProps={{ readOnly: true }}
						label={ts("name")}
					/>
				</Modal>
			)}

			{bulk.modals}

			<div className="relative w-full pr-0">
				<div className="pr-2 pb-2">
					<Table
						columns={tableColumns}
						data={data || []}
						deleteOrganizations={bulk.deleteAction}
						extraActions={bulk.extraActions}
						filter={filter}
						// setLimit goes back to page 1 itself; a setPage after it rewrote the old size (PREC-4403).
						handleChangeRowsPerPage={setLimit}
						handleFetchPage={(page) => {
							if (selectAll) {
								toggleSelectAll();
							}

							setPage(page);
						}}
						handleRowClick={handleRowClick}
						handleSorting={(sortParams: SortParams) => {
							setPage(1);
							handleSorting(sortParams);
						}}
						hidePagination={false}
						isLoading={isLoading || isFetching}
						limit={limit}
						overallCount={meta?.itemCount}
						page={page}
						clearSelection={() => setSelectedItems([])}
						selectedCount={selectAll ? meta?.itemCount : selectedItems.length}
						showFooterFunctions={true}
						tableConfig={tableConfig}
						totalPages={meta?.pageCount}
					/>
				</div>
			</div>
			{renderSnackbar()}
			{renderMessageSnackbar()}
		</div>
	);
};
